import { Currency, CurrencyAmount, Percent } from '@kodiak-finance/sdk-core'
import IUniswapV2Router02Json from '@kodiak-finance/v2-periphery/build/IUniswapV2Router02.json'
import { useQueryClient } from '@tanstack/react-query'
import PoweredByKodiak from 'components/PoweredByKodiak'
import UnsupportedCurrencyFooter from 'components/swap/UnsupportedCurrencyFooter'
import { chainIdToNativeCurrencyName } from 'config/chains'
import { V2_ROUTER_ADDRESSES } from 'constants/addresses'
import { v2QueryKeys } from 'hooks/subgraph/v2'
import { useChainAwareNavigate } from 'hooks/useChainAwareNavigate'
import { useConnection } from 'hooks/useConnection'
import { useCurrencyId } from 'hooks/useCurrencyId'
import { useResolveCurrencyAddress } from 'hooks/useResolveCurrencyAddress'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import { SwapWrapper } from 'pages/Swap'
import { useCallback, useContext, useState } from 'react'
import { Plus } from 'react-feather'
import { useDispatch } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import { Text } from 'rebass'
import styled from 'styled-components'
import { ThemeContext } from 'styled-components'
import type { Address } from 'viem'

import { ButtonError, ButtonLight, ButtonPrimary } from '../../components/Button'
import { BlueCard, LightCard } from '../../components/Card'
import { AutoColumn, ColumnCenter } from '../../components/Column'
import CurrencyInputPanel from '../../components/CurrencyInputPanel'
import DoubleCurrencyLogo from '../../components/DoubleLogo'
import { AddRemoveTabs } from '../../components/NavigationTabs'
import { MinimalPositionCard } from '../../components/PositionCard'
import Row, { RowBetween, RowFlat } from '../../components/Row'
import TransactionConfirmationModal, {
  ConfirmationModalContent,
  TransactionErrorContent,
} from '../../components/TransactionConfirmationModal'
import { ZERO_PERCENT } from '../../constants/misc'
import { WRAPPED_NATIVE_CURRENCY } from '../../constants/tokens'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallback } from '../../hooks/useApproveCallback'
import { useIsSwapUnsupported } from '../../hooks/useIsSwapUnsupported'
import useTransactionDeadline from '../../hooks/useTransactionDeadline'
import { PairState } from '../../hooks/useV2Pairs'
import { useToggleWalletModal } from '../../state/application/hooks'
import { Field } from '../../state/mint/actions'
import { useDerivedMintInfo, useMintActionHandlers, useMintState } from '../../state/mint/hooks'
import { useTransactionAdder } from '../../state/transactions/hooks'
import { TransactionType } from '../../state/transactions/types'
import { useUserSlippageToleranceWithDefault } from '../../state/user/hooks'
import { ThemedText } from '../../theme'
import { calculateSlippageAmount } from '../../utils/calculateSlippageAmount'
import { currencyId } from '../../utils/currencyId'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { Dots } from '../Pool/styleds'
import { ConfirmAddModalBottom } from './ConfirmAddModalBottom'
import { PoolPriceBar } from './PoolPriceBar'

const { abi: IUniswapV2Router02ABI } = IUniswapV2Router02Json

const DEFAULT_ADD_V2_SLIPPAGE_TOLERANCE = new Percent(50, 10_000)

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`

export const V2Wrapper = styled(SwapWrapper)`
  background-color: ${({ theme }) => theme.cardPrimary};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.09);
  padding: 24px;
  width: 100%;
`

interface TransactionState {
  attemptingTxn: boolean
  errorMessage: string | undefined
  txHash: string | undefined
  transactionWaiting?: boolean
}

export default function AddLiquidity() {
  const { currencyIdA, currencyIdB } = useParams<{ currencyIdA?: string; currencyIdB?: string }>()
  const navigate = useChainAwareNavigate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { account, chainId, wagmiWalletClient, wagmiPublicClient } = useConnection()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()

  const theme = useContext(ThemeContext)

  const resolvedCurrencyIdA = useResolveCurrencyAddress(currencyIdA)
  const resolvedCurrencyIdB = useResolveCurrencyAddress(currencyIdB)

  const currencyA = useCurrency(resolvedCurrencyIdA)
  const currencyB = useCurrency(
    resolvedCurrencyIdA?.toLowerCase() !== resolvedCurrencyIdB?.toLowerCase() ? resolvedCurrencyIdB : ''
  )
  const currencyAId = useCurrencyId(currencyA) ?? ''
  const currencyBId = useCurrencyId(currencyB) ?? ''

  const wrappedNativeCurrency = chainId ? WRAPPED_NATIVE_CURRENCY[chainId] : undefined

  const oneCurrencyIsWETH = Boolean(
    chainId &&
      wrappedNativeCurrency &&
      ((currencyA && currencyA.equals(wrappedNativeCurrency)) || (currencyB && currencyB.equals(wrappedNativeCurrency)))
  )

  const toggleWalletModal = useToggleWalletModal() // toggle wallet when disconnected

  // mint state
  const { independentField, typedValue, otherTypedValue } = useMintState()
  const {
    dependentField,
    currencies,
    pair,
    pairState,
    liquidityTokenBalance,
    totalSupply,
    currencyBalances,
    parsedAmounts,
    price,
    noLiquidity,
    liquidityMinted,
    poolTokenPercentage,
    error,
  } = useDerivedMintInfo(currencyA ?? undefined, currencyB ?? undefined)

  const { onFieldAInput, onFieldBInput } = useMintActionHandlers(noLiquidity)

  const isValid = !error

  // modal and loading
  const [transactionState, setTransactionState] = useState<TransactionState>({
    attemptingTxn: false,
    errorMessage: undefined,
    txHash: undefined,
    transactionWaiting: false,
  })

  const [showConfirm, setShowConfirm] = useState<boolean>(false)

  // txn values
  const deadline = useTransactionDeadline() // custom from users settings
  const allowedSlippage = useUserSlippageToleranceWithDefault(DEFAULT_ADD_V2_SLIPPAGE_TOLERANCE) // custom from users

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: noLiquidity ? otherTypedValue : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  // get the max amounts user can add
  const maxAmounts: { [field in Field]?: CurrencyAmount<Currency> } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: CurrencyAmount<Currency> } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )

  const routerAddress = V2_ROUTER_ADDRESSES[chainId]

  // check whether the user has approved the router on the tokens
  const [approvalA, approveACallback] = useApproveCallback(parsedAmounts[Field.CURRENCY_A], routerAddress)
  const [approvalB, approveBCallback] = useApproveCallback(parsedAmounts[Field.CURRENCY_B], routerAddress)

  const addTransaction = useTransactionAdder()
  const { writeContractAsync } = useWriteContract()

  async function onAdd() {
    // Set attempting state immediately to show loading
    setTransactionState({
      attemptingTxn: true,
      errorMessage: undefined,
      txHash: undefined,
      transactionWaiting: false,
    })

    try {
      if (!chainId || !wagmiWalletClient || !wagmiPublicClient || !account || !routerAddress) {
        throw new Error('Missing dependencies')
      }

      const { [Field.CURRENCY_A]: parsedAmountA, [Field.CURRENCY_B]: parsedAmountB } = parsedAmounts
      if (!parsedAmountA || !parsedAmountB || !currencyA || !currencyB || !deadline) {
        throw new Error('Missing required amounts or deadline')
      }

      const amountsMin = {
        [Field.CURRENCY_A]: calculateSlippageAmount(parsedAmountA, noLiquidity ? ZERO_PERCENT : allowedSlippage)[0],
        [Field.CURRENCY_B]: calculateSlippageAmount(parsedAmountB, noLiquidity ? ZERO_PERCENT : allowedSlippage)[0],
      }

      let functionName: string, args: any[], value: bigint | undefined

      if (currencyA.isNative || currencyB.isNative) {
        const tokenBIsETH = currencyB.isNative
        functionName = 'addLiquidityETH'
        args = [
          (tokenBIsETH ? currencyA : currencyB)?.wrapped?.address as Address, // token
          BigInt((tokenBIsETH ? parsedAmountA : parsedAmountB).quotient.toString()), // token desired
          BigInt(amountsMin[tokenBIsETH ? Field.CURRENCY_A : Field.CURRENCY_B].toString()), // token min
          BigInt(amountsMin[tokenBIsETH ? Field.CURRENCY_B : Field.CURRENCY_A].toString()), // eth min
          account,
          deadline,
        ]
        value = BigInt((tokenBIsETH ? parsedAmountB : parsedAmountA).quotient.toString())
      } else {
        functionName = 'addLiquidity'
        args = [
          currencyA?.wrapped?.address as Address,
          currencyB?.wrapped?.address as Address,
          BigInt(parsedAmountA.quotient.toString()),
          BigInt(parsedAmountB.quotient.toString()),
          BigInt(amountsMin[Field.CURRENCY_A].toString()),
          BigInt(amountsMin[Field.CURRENCY_B].toString()),
          account,
          BigInt(deadline.toString()),
        ]
        value = undefined
      }

      // Simulate the contract call first
      const { request } = await wagmiPublicClient.simulateContract({
        address: routerAddress,
        abi: IUniswapV2Router02ABI,
        functionName,
        args,
        account,
        value,
      })

      // Send the transaction
      const hash = await writeContractAsync(request)

      setTransactionState({
        attemptingTxn: false,
        errorMessage: undefined,
        txHash: hash,
        transactionWaiting: true,
      })

      addTransaction(
        { hash },
        {
          type: TransactionType.ADD_LIQUIDITY_V2_POOL,
          baseCurrencyId: currencyId(currencyA),
          expectedAmountBaseRaw: parsedAmounts[Field.CURRENCY_A]?.quotient.toString() ?? '0',
          quoteCurrencyId: currencyId(currencyB),
          expectedAmountQuoteRaw: parsedAmounts[Field.CURRENCY_B]?.quotient.toString() ?? '0',
        }
      )

      // Wait for transaction receipt (1 confirmation)
      const receipt = await waitForTransactionReceipt({ hash })

      setTransactionState({
        attemptingTxn: false,
        errorMessage: undefined,
        txHash: receipt.transactionHash,
        transactionWaiting: false,
      })

      if (account) {
        await queryClient.invalidateQueries({ queryKey: ['v2'] })

        if (pair?.liquidityToken?.address) {
          await queryClient.invalidateQueries({
            queryKey: v2QueryKeys.userPositionByPair(
              chainId,
              account.toLowerCase(),
              pair.liquidityToken.address.toLowerCase(),
              false
            ),
          })
        }
      }
    } catch (error: any) {
      setTransactionState({
        attemptingTxn: false,
        errorMessage: error?.reason || error?.message || 'Add liquidity failed',
        txHash: undefined,
        transactionWaiting: false,
      })

      // Log the error for debugging
      console.error('Add liquidity error:', error)
    }
  }

  const modalHeader = () => {
    return noLiquidity ? (
      <AutoColumn gap="20px">
        <LightCard mt="20px" mb="20px">
          <RowBetween>
            <Text fontSize="30px" fontWeight={500} lineHeight="32px" marginRight={20}>
              {currencies[Field.CURRENCY_A]?.symbol + '/' + currencies[Field.CURRENCY_B]?.symbol}
            </Text>
            <DoubleCurrencyLogo
              currency0={currencies[Field.CURRENCY_A]}
              currency1={currencies[Field.CURRENCY_B]}
              size={30}
            />
          </RowBetween>
        </LightCard>
      </AutoColumn>
    ) : (
      <AutoColumn gap="20px">
        <RowFlat style={{ marginTop: '20px', gap: '16px' }}>
          <Text fontSize="30px" fontWeight={500} lineHeight="32px" marginRight={10}>
            {liquidityMinted?.toSignificant(6)}
          </Text>
          <DoubleCurrencyLogo
            currency0={currencies[Field.CURRENCY_A]}
            currency1={currencies[Field.CURRENCY_B]}
            size={30}
          />
        </RowFlat>
        <Row>
          <Text fontSize="24px">
            {currencies[Field.CURRENCY_A]?.symbol + '/' + currencies[Field.CURRENCY_B]?.symbol + ' Pool Tokens'}
          </Text>
        </Row>
        <ThemedText.DeprecatedItalic fontSize={12} textAlign="left" padding="8px 0 0 0 ">
          <>
            Output is estimated. If the price changes by more than {allowedSlippage.toSignificant(4)}% your transaction
            will revert.
          </>
        </ThemedText.DeprecatedItalic>
      </AutoColumn>
    )
  }

  const modalBottom = () => {
    return (
      <ConfirmAddModalBottom
        price={price}
        currencies={currencies}
        parsedAmounts={parsedAmounts}
        noLiquidity={noLiquidity}
        onAdd={onAdd}
        poolTokenPercentage={poolTokenPercentage}
      />
    )
  }

  const pendingText = (
    <>
      Supplying {parsedAmounts[Field.CURRENCY_A]?.toSignificant(6)} {currencies[Field.CURRENCY_A]?.symbol} and{' '}
      {parsedAmounts[Field.CURRENCY_B]?.toSignificant(6)} {currencies[Field.CURRENCY_B]?.symbol}
    </>
  )

  const handleCurrencyASelect = useCallback(
    (currencyA: Currency) => {
      const newCurrencyIdA = currencyId(currencyA, chainId)
      if (newCurrencyIdA === currencyBId) {
        navigate(`/liquidity/v2/add/${currencyBId}/${currencyAId}`)
      } else {
        navigate(`/liquidity/v2/add/${newCurrencyIdA}/${currencyBId}`)
      }
    },
    [currencyBId, navigate, currencyAId, chainId]
  )
  const handleCurrencyBSelect = useCallback(
    (currencyB: Currency) => {
      const newCurrencyIdB = currencyId(currencyB, chainId)
      if (currencyAId === newCurrencyIdB) {
        if (currencyBId) {
          navigate(`/liquidity/v2/add/${currencyBId}/${newCurrencyIdB}`)
        } else {
          navigate(`/liquidity/v2/add/${newCurrencyIdB}`)
        }
      } else {
        navigate(
          `/liquidity/v2/add/${currencyAId ? currencyAId : chainIdToNativeCurrencyName[chainId]}/${newCurrencyIdB}`
        )
      }
    },
    [currencyAId, navigate, currencyBId, chainId]
  )

  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false)
    // if there was a tx hash, we want to clear the input
    if (transactionState.txHash) {
      onFieldAInput('')
    }
    setTransactionState({
      attemptingTxn: false,
      errorMessage: undefined,
      txHash: undefined,
      transactionWaiting: false,
    })
  }, [onFieldAInput, transactionState.txHash])

  const confirmationContent = useCallback(() => {
    if (transactionState.errorMessage) {
      return <TransactionErrorContent onDismiss={handleDismissConfirmation} message={transactionState.errorMessage} />
    }
    if (transactionState.txHash) {
      return (
        <ConfirmationModalContent
          title={noLiquidity ? <>You are creating a pool</> : <>You will receive</>}
          onDismiss={handleDismissConfirmation}
          topContent={() => (
            <div>
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <div style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>Transaction Submitted</div>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                  Supplying {parsedAmounts[Field.CURRENCY_A]?.toSignificant(6)} {currencies[Field.CURRENCY_A]?.symbol}{' '}
                  and {parsedAmounts[Field.CURRENCY_B]?.toSignificant(6)} {currencies[Field.CURRENCY_B]?.symbol}
                </div>
              </div>
            </div>
          )}
          bottomContent={() => null}
        />
      )
    }
    return (
      <ConfirmationModalContent
        title={noLiquidity ? <>You are creating a pool</> : <>You will receive</>}
        onDismiss={handleDismissConfirmation}
        topContent={modalHeader}
        bottomContent={modalBottom}
      />
    )
  }, [
    transactionState.errorMessage,
    transactionState.txHash,
    handleDismissConfirmation,
    noLiquidity,
    parsedAmounts,
    currencies,
  ])

  const { pathname } = useLocation()
  const isCreate = pathname.includes('/create')

  const addIsUnsupported = useIsSwapUnsupported(currencies?.CURRENCY_A, currencies?.CURRENCY_B)

  return (
    <>
      <V2Wrapper>
        <AddRemoveTabs creating={isCreate} adding={true} defaultSlippage={DEFAULT_ADD_V2_SLIPPAGE_TOLERANCE} />
        <Wrapper>
          <TransactionConfirmationModal
            isOpen={showConfirm}
            onDismiss={handleDismissConfirmation}
            attemptingTxn={transactionState.attemptingTxn}
            hash={transactionState.txHash || ''}
            transactionWaiting={transactionState.transactionWaiting}
            content={confirmationContent}
            pendingText={pendingText}
            currencyToAdd={pair?.liquidityToken}
          />
          <AutoColumn gap="20px">
            {noLiquidity ||
              (isCreate ? (
                <ColumnCenter>
                  <BlueCard>
                    <AutoColumn gap="10px">
                      <ThemedText.DeprecatedLink fontWeight={600} color="deprecated_primaryText1">
                        <>You are the first liquidity provider.</>
                      </ThemedText.DeprecatedLink>
                      <ThemedText.DeprecatedLink fontWeight={400} color="deprecated_primaryText1">
                        <>The ratio of tokens you add will set the price of this pool.</>
                      </ThemedText.DeprecatedLink>
                      <ThemedText.DeprecatedLink fontWeight={400} color="deprecated_primaryText1">
                        <>Once you are happy with the rate click supply to review.</>
                      </ThemedText.DeprecatedLink>
                    </AutoColumn>
                  </BlueCard>
                </ColumnCenter>
              ) : (
                <ColumnCenter>
                  <BlueCard>
                    <AutoColumn gap="10px">
                      <ThemedText.DeprecatedLink fontWeight={400} color="deprecated_primaryText1">
                        <>
                          <b>
                            <>Tip:</>
                          </b>{' '}
                          When you add liquidity, you will receive pool tokens representing your position. These tokens
                          automatically earn fees proportional to your share of the pool, and can be redeemed at any
                          time.
                        </>
                      </ThemedText.DeprecatedLink>
                    </AutoColumn>
                  </BlueCard>
                </ColumnCenter>
              ))}

            {!addIsUnsupported && pair && !noLiquidity && pairState !== PairState.INVALID && (
              <AutoColumn style={{ minWidth: '20rem', width: '100%', maxWidth: '400px', marginTop: '1rem' }}>
                <MinimalPositionCard
                  showUnwrapped={oneCurrencyIsWETH}
                  pair={pair}
                  liquidityTokenBalance={liquidityTokenBalance}
                  totalSupply={totalSupply}
                />
              </AutoColumn>
            )}
            <CurrencyInputPanel
              value={formattedAmounts[Field.CURRENCY_A]}
              onUserInput={onFieldAInput}
              onMax={() => {
                onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
              }}
              onCurrencySelect={handleCurrencyASelect}
              showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
              currency={currencies[Field.CURRENCY_A] ?? null}
              id="add-liquidity-input-tokena"
              showCommonBases
            />
            <ColumnCenter>
              <Plus size="16" color={theme.deprecated_text2} />
            </ColumnCenter>
            <CurrencyInputPanel
              value={formattedAmounts[Field.CURRENCY_B]}
              onUserInput={onFieldBInput}
              onCurrencySelect={handleCurrencyBSelect}
              onMax={() => {
                onFieldBInput(maxAmounts[Field.CURRENCY_B]?.toExact() ?? '')
              }}
              showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
              currency={currencies[Field.CURRENCY_B] ?? null}
              id="add-liquidity-input-tokenb"
              showCommonBases
            />
            {currencies[Field.CURRENCY_A] && currencies[Field.CURRENCY_B] && pairState !== PairState.INVALID && (
              <>
                <LightCard padding="0px" $borderRadius="8px">
                  <RowBetween padding="1rem">
                    <ThemedText.DeprecatedSubHeader fontWeight={500} fontSize={14}>
                      {noLiquidity ? <>Initial prices and pool share</> : <>Prices and pool share</>}
                    </ThemedText.DeprecatedSubHeader>
                  </RowBetween>{' '}
                  <LightCard padding="1rem" $borderRadius="0">
                    <PoolPriceBar
                      currencies={currencies}
                      poolTokenPercentage={poolTokenPercentage}
                      noLiquidity={noLiquidity}
                      price={price}
                    />
                  </LightCard>
                </LightCard>
              </>
            )}

            {addIsUnsupported ? (
              <ButtonPrimary disabled={true}>
                <ThemedText.DeprecatedMain mb="4px">
                  <>Unsupported Asset</>
                </ThemedText.DeprecatedMain>
              </ButtonPrimary>
            ) : !account ? (
              <ButtonLight onClick={toggleWalletModal}>
                <>Connect Wallet</>
              </ButtonLight>
            ) : (
              <AutoColumn gap="md">
                {(approvalA === ApprovalState.NOT_APPROVED ||
                  approvalA === ApprovalState.PENDING ||
                  approvalB === ApprovalState.NOT_APPROVED ||
                  approvalB === ApprovalState.PENDING) &&
                  isValid && (
                    <RowBetween>
                      {approvalA !== ApprovalState.APPROVED && (
                        <ButtonPrimary
                          onClick={approveACallback}
                          disabled={approvalA === ApprovalState.PENDING}
                          width={approvalB !== ApprovalState.APPROVED ? '48%' : '100%'}
                        >
                          {approvalA === ApprovalState.PENDING ? (
                            <Dots>
                              <>Approving {currencies[Field.CURRENCY_A]?.symbol}</>
                            </Dots>
                          ) : (
                            <>Approve {currencies[Field.CURRENCY_A]?.symbol}</>
                          )}
                        </ButtonPrimary>
                      )}
                      {approvalB !== ApprovalState.APPROVED && (
                        <ButtonPrimary
                          onClick={approveBCallback}
                          disabled={approvalB === ApprovalState.PENDING}
                          width={approvalA !== ApprovalState.APPROVED ? '48%' : '100%'}
                        >
                          {approvalB === ApprovalState.PENDING ? (
                            <Dots>
                              <>Approving {currencies[Field.CURRENCY_B]?.symbol}</>
                            </Dots>
                          ) : (
                            <>Approve {currencies[Field.CURRENCY_B]?.symbol}</>
                          )}
                        </ButtonPrimary>
                      )}
                    </RowBetween>
                  )}
                <ButtonError
                  onClick={() => {
                    setShowConfirm(true)
                  }}
                  disabled={!isValid || approvalA !== ApprovalState.APPROVED || approvalB !== ApprovalState.APPROVED}
                  error={!isValid && !!parsedAmounts[Field.CURRENCY_A] && !!parsedAmounts[Field.CURRENCY_B]}
                >
                  <Text fontSize={20} fontWeight={500}>
                    {error ?? <>Supply</>}
                  </Text>
                </ButtonError>
              </AutoColumn>
            )}
          </AutoColumn>
        </Wrapper>
      </V2Wrapper>

      {addIsUnsupported && (
        <UnsupportedCurrencyFooter
          show={addIsUnsupported}
          currencies={[currencies.CURRENCY_A, currencies.CURRENCY_B]}
        />
      )}
      <PoweredByKodiak />
    </>
  )
}
